import React, { useState } from "react";
import { Alert, Tooltip } from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  WarningFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { EuerVatTaxAccountDirection } from "../../../../../../../../api/graphql/schema.generated";
import { formatAmountInCents } from "../../../../../../../../utils";
import { EuerVatTaxAccountCheckState } from "./useEuerVatTaxAccountCheck";
import {
  CheckBlock,
  CheckBox,
  CheckTitle,
  CheckBoxCol,
  CheckFooter,
  CheckLinkButton,
  CheckMatch,
  CheckMismatch,
  CheckNetBlock,
  CheckNetGrid,
  CheckNetRow,
  CheckTable,
} from "./styledComponents";

export interface EuerVatTaxAccountCheckProps {
  /** 186 = VAT paid to the tax office, 141 = VAT refunded by it. */
  code: "186" | "141";
  /** Current field value in euros (what the operator sees, incl. edits). */
  fieldValue: number;
  /** Field 186 (VAT paid) in euros, incl. edits, for the net lines. */
  paidFieldValue: number;
  /** Field 141 (VAT refunded) in euros, incl. edits, for the net lines. */
  refundedFieldValue: number;
  check: EuerVatTaxAccountCheckState;
}

const NO_TAX_NUMBER_STATUS = 404;

const useCheckLink = (check: EuerVatTaxAccountCheckState) => {
  const { t } = useTranslation("EuerVatTaxAccountCheck");
  return (label: string) => (
    <Tooltip title={t("hint")}>
      <CheckLinkButton
        type="link"
        size="small"
        // Muted while the lookup runs, so it cannot be started twice.
        disabled={check.loading}
        loading={check.loading}
        onClick={check.check}
      >
        {label}
      </CheckLinkButton>
    </Tooltip>
  );
};

const getLine = (check: EuerVatTaxAccountCheckState, code: "186" | "141") =>
  check.result
    ? code === "186"
      ? check.result.paid
      : check.result.refunded
    : null;

/**
 * True when the tax account could not tell payments from refunds and has
 * entries to show. Without entries there is nothing unclear to look at, so
 * the normal comparison (0 against the field value) applies.
 */
const isDirectionUnclear = (check: EuerVatTaxAccountCheckState) =>
  check.result?.direction === EuerVatTaxAccountDirection.UNCLEAR &&
  check.result.unclear.entries.length > 0;

/** The one short line in the amount column. */
const EuerVatTaxAccountCheck = ({
  code,
  fieldValue,
  check,
}: EuerVatTaxAccountCheckProps) => {
  const { t } = useTranslation("EuerVatTaxAccountCheck");
  const link = useCheckLink(check);
  const line = getLine(check, code);

  if ((check.error && !check.loading) || !line) {
    return <CheckBlock>{link(t("label"))}</CheckBlock>;
  }

  if (isDirectionUnclear(check)) {
    return <CheckBlock>{t("unclearShort")}</CheckBlock>;
  }

  const difference = Math.round(fieldValue * 100) - line.total;

  return (
    <CheckBlock>
      {t("short", { amount: formatAmountInCents(line.total, true) })}{" "}
      {difference === 0 ? (
        <Tooltip title={t("match")}>
          <CheckMatch aria-label={String(t("match"))}>
            <CheckCircleFilled />
          </CheckMatch>
        </Tooltip>
      ) : (
        <Tooltip
          title={t("mismatch", {
            difference: formatAmountInCents(difference, true),
          })}
        >
          <CheckMismatch aria-label={String(t("mismatchLabel"))}>
            <WarningFilled />
          </CheckMismatch>
        </Tooltip>
      )}
    </CheckBlock>
  );
};

export type EuerVatTaxAccountCheckBoxProps = EuerVatTaxAccountCheckProps;

/** The info box under the list item, aligned with the "Bezeichnung" column. */
export const EuerVatTaxAccountCheckBox = ({
  code,
  fieldValue,
  paidFieldValue,
  refundedFieldValue,
  check,
}: EuerVatTaxAccountCheckBoxProps) => {
  const { t } = useTranslation("EuerVatTaxAccountCheck");
  const [open, setOpen] = useState(true);
  const link = useCheckLink(check);
  const { error, loading, result } = check;
  const line = getLine(check, code);

  if (error && !loading) {
    const noTaxNumber = error.graphQLErrors?.some(
      (graphQLError) =>
        (graphQLError.extensions as { status?: number } | undefined)?.status ===
        NO_TAX_NUMBER_STATUS
    );
    return (
      <CheckBoxCol offset={3} span={21}>
        <Alert
          type="error"
          showIcon
          message={
            <>
              {noTaxNumber ? t("noTaxNumber") : t("failed")}{" "}
              {link(t("recheck"))}
            </>
          }
        />
      </CheckBoxCol>
    );
  }

  if (!line || !result) {
    return null;
  }

  const unclear = isDirectionUnclear(check);
  // In the unclear state the list shows the entries whose direction is open.
  const shownLine = unclear ? result.unclear : line;
  const difference = Math.round(fieldValue * 100) - line.total;
  const matches = difference === 0;
  const boxType = unclear ? "info" : matches ? "success" : "warning";

  // The net is only meaningful once payments and refunds can be told apart.
  const showNet = result.direction !== EuerVatTaxAccountDirection.UNCLEAR;
  const euerPaid = Math.round(paidFieldValue * 100);
  const euerRefunded = Math.round(refundedFieldValue * 100);
  const euerNet = euerPaid - euerRefunded;
  const accountPaid = result.paid.total;
  const accountRefunded = result.refunded.total;
  const accountNet = accountPaid - accountRefunded;
  const netDifference = euerNet - accountNet;

  const columns = [
    { title: t("columnPeriod"), dataIndex: "period" },
    { title: t("columnDate"), dataIndex: "date" },
    {
      title: t("columnAmount"),
      dataIndex: "amount",
      align: "right" as const,
      render: (_: unknown, entry: (typeof shownLine.entries)[number]) =>
        formatAmountInCents(entry.amount, true),
    },
    { title: t("columnExplanation"), dataIndex: "explanation" },
  ];

  return (
    <CheckBoxCol offset={3} span={21}>
      <CheckBox role="alert" data-type={boxType} $type={boxType}>
        <CheckTitle>
          {unclear ? (
            <span>{t("directionUnclear")}</span>
          ) : (
            <>
              {matches ? (
                <CheckMatch as="span">
                  <CheckCircleFilled />
                </CheckMatch>
              ) : (
                <CheckMismatch as="span">
                  <ExclamationCircleFilled />
                </CheckMismatch>
              )}
              <span>
                {`${t("boxTitle", {
                  amount: formatAmountInCents(line.total, true),
                })} – ${
                  matches
                    ? t("boxMatch")
                    : t("boxDifference", {
                        difference: formatAmountInCents(difference, true),
                      })
                }`}
              </span>
            </>
          )}
        </CheckTitle>
        {open && (
          <>
            {shownLine.entries.length === 0 ? (
              <div>{t("noEntries")}</div>
            ) : (
              <CheckTable
                size="small"
                pagination={false}
                columns={columns as any}
                dataSource={shownLine.entries}
                rowKey={(_: unknown, index?: number) => String(index)}
              />
            )}
          </>
        )}
        {showNet && (
          <CheckNetBlock>
            <CheckNetRow>
              {netDifference === 0 ? (
                <CheckMatch as="span">
                  <CheckCircleFilled />
                </CheckMatch>
              ) : (
                <CheckMismatch as="span">
                  <ExclamationCircleFilled />
                </CheckMismatch>
              )}
              <span>
                {t("netTitle")}
                {` – ${
                  netDifference === 0
                    ? t("netMatch")
                    : t("netDifference", {
                        difference: formatAmountInCents(netDifference, true),
                      })
                }`}
              </span>
            </CheckNetRow>
            <CheckNetGrid>
              <span>{t("netEuerLabel")}</span>
              <span>
                {t("netCalculation", {
                  paid: formatAmountInCents(euerPaid, true),
                  refunded: formatAmountInCents(euerRefunded, true),
                  net: formatAmountInCents(euerNet, true),
                })}
              </span>
              <span>{t("netAccountLabel")}</span>
              <span>
                {t("netCalculation", {
                  paid: formatAmountInCents(accountPaid, true),
                  refunded: formatAmountInCents(accountRefunded, true),
                  net: formatAmountInCents(accountNet, true),
                })}
              </span>
            </CheckNetGrid>
          </CheckNetBlock>
        )}
        <CheckFooter>
          {t("source", {
            taxNumber: result.taxNumber,
            queryDate: result.queryDate || "-",
          })}
          {" · "}
          {link(t("recheck"))}
          {" · "}
          <CheckLinkButton
            type="link"
            size="small"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {open
              ? t("collapse")
              : t("expand", { count: shownLine.entries.length })}
          </CheckLinkButton>
        </CheckFooter>
      </CheckBox>
    </CheckBoxCol>
  );
};

export default EuerVatTaxAccountCheck;
